import * as callsLiveDeviceEvidenceAuthorityModule
  from './CallsLiveDeviceEvidenceAuthority';

export type CallsLiveDeviceEvidenceVerificationStatus =
  | 'verified'
  | 'unverified';

export type CallsLiveDeviceEvidenceRequirement =
  | 'authorized-evidence'
  | 'device-identity'
  | 'fcm-registration'
  | 'notification-channel'
  | 'push-delivery'
  | 'heads-up-or-full-screen'
  | 'incoming-call-screen'
  | 'answer-or-decline'
  | 'call-end-or-missed-call';

export interface CallsLiveDeviceEvidenceCheck {
  readonly requirement: CallsLiveDeviceEvidenceRequirement;
  readonly passed: boolean;
  readonly evidence: string;
}

export interface CallsLiveDeviceEvidenceVerificationResult {
  readonly engine: 'CallsLiveDeviceEvidenceVerificationEngine';
  readonly evidenceSource: 'CallsLiveDeviceEvidenceAuthority';
  readonly status: CallsLiveDeviceEvidenceVerificationStatus;
  readonly verified: boolean;
  readonly authorized: boolean;
  readonly checksPassed: number;
  readonly checksRequired: number;
  readonly missingEvidence: readonly CallsLiveDeviceEvidenceRequirement[];
  readonly checks: readonly CallsLiveDeviceEvidenceCheck[];
  readonly verifiedAt: string;
}

type UnknownRecord = Readonly<Record<string, unknown>>;
type AuthorityFunction = () => unknown;

const AUTHORITY_FUNCTION_CANDIDATES = Object.freeze([
  'authorizeCallsLiveDeviceEvidence',
  'getCallsLiveDeviceEvidenceAuthority',
  'evaluateCallsLiveDeviceEvidenceAuthority',
  'runCallsLiveDeviceEvidenceAuthority',
  'verifyCallsLiveDeviceEvidenceAuthority',
]);

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null;
}

function flattenEvidence(
  value: unknown,
  visited: Set<unknown> = new Set(),
): string {
  if (
    value === null ||
    value === undefined ||
    typeof value === 'function'
  ) {
    return '';
  }

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value).toLowerCase();
  }

  if (visited.has(value)) {
    return '';
  }

  visited.add(value);

  if (Array.isArray(value)) {
    return value
      .map((entry) => flattenEvidence(entry, visited))
      .join(' ');
  }

  if (isRecord(value)) {
    return Object.entries(value)
      .map(
        ([key, entry]) =>
          `${key.toLowerCase()} ${flattenEvidence(entry, visited)}`,
      )
      .join(' ');
  }

  return '';
}

function resolveAuthorityEvidence(): unknown {
  const moduleRecord =
    callsLiveDeviceEvidenceAuthorityModule as unknown as Record<
      string,
      unknown
    >;

  for (const candidate of AUTHORITY_FUNCTION_CANDIDATES) {
    const possibleFunction = moduleRecord[candidate];

    if (typeof possibleFunction === 'function') {
      return (possibleFunction as AuthorityFunction)();
    }
  }

  const exportedFunction = Object.values(moduleRecord).find(
    (value): value is AuthorityFunction =>
      typeof value === 'function',
  );

  if (exportedFunction) {
    return exportedFunction();
  }

  return moduleRecord;
}

function includesAny(
  evidenceText: string,
  terms: readonly string[],
): boolean {
  return terms.some((term) => evidenceText.includes(term));
}

function determineAuthorized(
  authorityEvidence: unknown,
  evidenceText: string,
): boolean {
  if (isRecord(authorityEvidence)) {
    if (authorityEvidence.authorized === true) return true;
    if (authorityEvidence.verified === true) return true;

    const status = String(authorityEvidence.status ?? '').toLowerCase();
    const decision = String(authorityEvidence.decision ?? '').toLowerCase();
    const authority = String(authorityEvidence.authority ?? '').toLowerCase();

    if (
      status === 'authorized' ||
      status === 'verified' ||
      decision === 'allow' ||
      decision === 'authorized' ||
      authority === 'authorized'
    ) {
      return true;
    }
  }

  return includesAny(evidenceText, [
    'authorized true',
    'verified true',
    'decision allow',
    'status authorized',
    'status verified',
  ]);
}

function createCheck(
  requirement: CallsLiveDeviceEvidenceRequirement,
  passed: boolean,
  evidence: string,
): CallsLiveDeviceEvidenceCheck {
  return Object.freeze({
    requirement,
    passed,
    evidence,
  });
}

export function verifyCallsLiveDeviceEvidence():
CallsLiveDeviceEvidenceVerificationResult {
  const authorityEvidence = resolveAuthorityEvidence();
  const evidenceText = flattenEvidence(authorityEvidence);
  const authorized = determineAuthorized(
    authorityEvidence,
    evidenceText,
  );

  const checks = Object.freeze([
    createCheck(
      'authorized-evidence',
      authorized,
      authorized
        ? 'CG-021 authorized the live-device evidence.'
        : 'CG-021 authorization was not proven.',
    ),
    createCheck(
      'device-identity',
      includesAny(evidenceText, [
        'deviceid',
        'device id',
        'receiverdevice',
        'receiver device',
        'android device',
      ]),
      'Receiver/device identity must be present.',
    ),
    createCheck(
      'fcm-registration',
      includesAny(evidenceText, [
        'fcmtoken',
        'fcm token',
        'push token',
        'registration token',
        'firebase token',
      ]),
      'Native FCM registration evidence must be present.',
    ),
    createCheck(
      'notification-channel',
      includesAny(evidenceText, [
        'notificationchannelready true',
        'notification channel ready true',
        'incoming_call',
        'incoming-call channel',
        'importance_high',
        'importance high',
      ]),
      'High-priority incoming-call notification channel must be proven.',
    ),
    createCheck(
      'push-delivery',
      includesAny(evidenceText, [
        'push-delivered',
        'push delivered',
        'push-received',
        'push received',
        'fcm delivered',
      ]),
      'Receiver push delivery must be proven.',
    ),
    createCheck(
      'heads-up-or-full-screen',
      includesAny(evidenceText, [
        'heads-up-visible',
        'heads up visible',
        'fullscreenintent',
        'full screen intent',
        'full-screen intent',
        'lock-screen wake',
        'screen woke',
      ]),
      'Heads-up, lock-screen wake, or full-screen intent evidence is required.',
    ),
    createCheck(
      'incoming-call-screen',
      includesAny(evidenceText, [
        'call-screen-opened',
        'call screen opened',
        'incomingcallactivity',
        'incoming call activity',
        'incoming screen',
      ]),
      'Incoming-call screen launch must be proven.',
    ),
    createCheck(
      'answer-or-decline',
      includesAny(evidenceText, [
        'answered',
        'answer event',
        'accepted',
        'declined',
        'decline event',
      ]),
      'A live answer or decline control result must be proven.',
    ),
    createCheck(
      'call-end-or-missed-call',
      includesAny(evidenceText, [
        'call ended',
        'ended',
        'missed-call-recorded',
        'missed call recorded',
        'post-call',
      ]),
      'Call termination or missed-call recording must be proven.',
    ),
  ]);

  const missingEvidence = Object.freeze(
    checks
      .filter((check) => !check.passed)
      .map((check) => check.requirement),
  );

  const checksPassed = checks.length - missingEvidence.length;
  const verified = authorized && missingEvidence.length === 0;

  return Object.freeze({
    engine: 'CallsLiveDeviceEvidenceVerificationEngine',
    evidenceSource: 'CallsLiveDeviceEvidenceAuthority',
    status: verified ? 'verified' : 'unverified',
    verified,
    authorized,
    checksPassed,
    checksRequired: checks.length,
    missingEvidence,
    checks,
    verifiedAt: new Date().toISOString(),
  });
}
